// SCR-SETTINGS (служебный стек, ux/11 §2.1) и его подэкраны SCR-ACCOUNT, SCR-SUPPORT, SCR-LEGAL (E02-T09, ux/11 §2.4–2.5).
// Изменения применяются сразу, без «Сохранить». Звук, вибрация, рука, тема, размер текста, движение, батарея, реклама —
// на устройстве и на сервере (PATCH /me {settings}, Р-100); «хроника» — PATCH /me {hideFromChronicle}; сообщения от бота —
// GET/PUT /me/notifications. Удаление аккаунта и выгрузка данных — Р-90. Решения клиента — CONTRACT Р-К101.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import type { ComponentChildren } from 'preact';
import { useApp } from '../app/app.js';
import { CLIENT_BUILD } from '../app/app.js';
import { sfxVolume } from '../audio/sfx.js';
import { DELETE_CONFIRM } from '../content/account.js';
import { mskTime } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { clientConfig, online, player } from '../state/game.js';
import { applyPrefs, batterySaver, deviceSettings, reduceMotion, TEXT_SIZES, textSize, type TextSize } from '../state/prefs.js';
import { storage } from '../state/storage.js';
import { applyTheme, type ThemeChoice } from '../telegram/theme.js';
import { canToggleFullscreen, FULLSCREEN_KEY, setFullscreen } from '../telegram/viewport.js';
import { stage, type ScreenProps } from '../ui/App.js';
import { ScreenTitle, useActionBar } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { GAME_LINK } from './system.js';
import { ChatRulesConsent } from './chat.js';
import { chatRulesAccepted, legalUrls, markChatRulesAccepted } from '../state/legal.js';

type Notif = ResponseOf<Api['getNotifications']>;

function Toggle(p: { label: string; hint?: string; on: boolean; onChange: (v: boolean) => void; testId: string; disabled?: boolean }) {
  const app = useApp();
  return (
    <label class="list-row set-row">
      <span class="col" style={{ flex: 1, gap: 2 }}><span>{p.label}</span>{p.hint ? <span class="small muted">{p.hint}</span> : null}</span>
      <input type="checkbox" role="switch" checked={p.on} disabled={p.disabled} data-testid={p.testId} class="switch"
        onChange={(e) => { app.haptic('selection'); p.onChange((e.target as HTMLInputElement).checked); }} />
    </label>
  );
}

function Slider(p: { label: string; value: number; onChange: (v: number) => void; testId: string }) {
  return (
    <label class="list-row set-row col" style={{ alignItems: 'stretch', gap: 4 }}>
      <span class="row"><span style={{ flex: 1 }}>{p.label}</span><span class="small muted">{p.value}</span></span>
      <input type="range" min={0} max={100} step={5} value={p.value} data-testid={p.testId} onInput={(e) => p.onChange(Number((e.target as HTMLInputElement).value))} />
    </label>
  );
}

function Group(p: { title: string; children: ComponentChildren }) {
  return <section class="set-group"><h3 class="small muted">{p.title}</h3><div class="list">{p.children}</div></section>;
}

function Link(p: { label: string; testId: string; onClick: () => void; right?: string; disabled?: boolean }) {
  return <button class="list-row set-row" data-testid={p.testId} disabled={p.disabled} onClick={p.onClick}><span style={{ flex: 1 }}>{p.label}</span>{p.right ? <span class="small muted">{p.right}</span> : null}{p.disabled ? null : '›'}</button>;
}

export function SettingsScreen(_p: ScreenProps) {
  const app = useApp();
  const num = (k: string, d: number) => { const v = Number(storage.get(k) ?? d); return Number.isFinite(v) ? v : d; };
  const [music, setMusic] = useState(num('musicVolume', 60));
  const [sfx, setSfx] = useState(sfxVolume());
  const [full, setFull] = useState(storage.get(FULLSCREEN_KEY) !== 'off');
  const [vib, setVib] = useState(storage.get('vibration') !== 'off');
  const [left, setLeft] = useState(storage.get('hand') === 'left');
  const [theme, setTheme] = useState<ThemeChoice>((storage.get('theme') as ThemeChoice) ?? 'telegram');
  const [text, setText] = useState<TextSize>(textSize());
  const [motion, setMotion] = useState(reduceMotion());
  const [battery, setBattery] = useState(batterySaver());
  const [ads, setAds] = useState(storage.get('ads') !== 'off');
  const [chronicle, setChronicle] = useState(!!player.value?.hideFromChronicle);
  const [notif, setNotif] = useState<Notif | null>(null);
  useActionBar({ back: true });
  const save = (k: string, v: string) => { storage.set(k, v); applyPrefs(); };
  // Переносимые настройки — на сервер через 0.5 с после последнего изменения (ползунки шлют много событий).
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sync = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      app.call('updateMe', { body: { settings: deviceSettings() } }).then((p) => { player.value = p; }).catch(() => {});
    }, 500);
  };
  useEffect(() => { app.call('getNotifications').then(setNotif).catch(() => {}); return () => { if (timer.current) { clearTimeout(timer.current); void app.call('updateMe', { body: { settings: deviceSettings() } }).catch(() => {}); } }; }, []);
  const setChannels = (ch: Partial<Notif['channels']>, writeAccessResult?: 'allowed' | 'denied') => {
    app.call('putNotifications', { body: { channels: ch, ...(writeAccessResult ? { writeAccessResult } : {}) } }).then(setNotif).catch((e) => app.handleError(e));
  };
  const askNotify = (v: boolean) => {
    const all = { trip_done: v, coop_call: v, raid_start: v, energy_full: v, news: v };
    if (!v) { setChannels(all); return; }
    // Разрешение писать боту — системное окно Telegram (requestWriteAccess); итог — на сервер.
    const wa = app.wa;
    if (!wa?.requestWriteAccess || notif?.botWriteAccess) { setChannels(all); return; }
    wa.requestWriteAccess((granted) => { setChannels(granted ? all : {}, granted ? 'allowed' : 'denied'); if (!granted) showToast(tr('CL-SET-NOTIFY-DENIED')); });
  };
  const notifyOn = !!notif && notif.botWriteAccess && Object.values(notif.channels).some(Boolean);
  const legal = clientConfig.value['balanceVersion'];
  return (
    <>
      <ScreenTitle>{tr('UI-SET-TITLE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-SETTINGS">
        <div class="page col set-page">
          <Group title={tr('CL-SET-SOUND')}>
            <Slider label={tr('CL-SET-MUSIC')} value={music} testId="set-music" onChange={(v) => { setMusic(v); storage.set('musicVolume', String(v)); sync(); }} />
            <Slider label={tr('CL-SET-SFX')} value={sfx} testId="set-sfx" onChange={(v) => { setSfx(v); storage.set('sfxVolume', String(v)); sync(); }} />
          </Group>
          <Group title={tr('CL-SET-FEEL')}>
            <Toggle label={tr('UI-SET-HAPTICS')} testId="set-vibration" on={vib} onChange={(v) => { setVib(v); storage.set('vibration', v ? 'on' : 'off'); if (v) app.haptic('medium'); sync(); }} />
            <Toggle label={tr('CL-SCREENS-SETTINGS-01')} testId="set-hand" on={left} onChange={(v) => { setLeft(v); storage.set('hand', v ? 'left' : 'right'); sync(); }} />
          </Group>
          <Group title={tr('CL-SET-SCREEN')}>
            <div class="list-row set-row">
              <span style={{ flex: 1 }}>{tr('UI-SET-THEME')}</span>
              <span class="chips">
                {(['telegram', 'light', 'dark'] as const).map((t) => (
                  <button key={t} class={`chip ${theme === t ? 'on' : ''}`} data-testid={`set-theme-${t}`} onClick={() => { setTheme(t); storage.set('theme', t); applyTheme(app.wa); sync(); }}>
                    {t === 'telegram' ? tr('UI-SET-THEME-SYSTEM') : t === 'light' ? tr('CL-SCREENS-SETTINGS-02') : tr('CL-SCREENS-SETTINGS-03')}
                  </button>
                ))}
              </span>
            </div>
            <div class="list-row set-row">
              <span style={{ flex: 1 }}>{tr('CL-SET-TEXT')}</span>
              <span class="chips">{TEXT_SIZES.map((s) => <button key={s} class={`chip ${text === s ? 'on' : ''}`} data-testid={`set-text-${s}`} onClick={() => { setText(s); save('textSize', String(s)); }}>{s}%</button>)}</span>
            </div>
            <Toggle label={tr('CL-SET-MOTION')} testId="set-motion" on={motion} onChange={(v) => { setMotion(v); save('reduceMotion', v ? 'on' : 'off'); }} />
            {canToggleFullscreen(app.wa) ? <Toggle label={tr('UI-SET-FULLSCREEN')} testId="set-fullscreen" on={full} onChange={(v) => { setFull(v); setFullscreen(app.wa, v, 'SCR-SETTINGS'); }} /> : null}
            <Toggle label={tr('CL-SET-BATTERY')} hint={tr('CL-SET-BATTERY-HINT')} testId="set-battery" on={battery} onChange={(v) => { setBattery(v); save('battery', v ? 'on' : 'off'); applyFps(); }} />
          </Group>
          <Group title={tr('CL-SET-NOTIFY-GROUP')}>
            <Toggle label={tr('CL-SET-NOTIFY')} hint={tr('CL-SET-NOTIFY-HINT')} testId="set-notify" on={notifyOn} disabled={!notif || !online.value} onChange={askNotify} />
            {notifyOn ? (Object.keys(notif!.channels) as (keyof Notif['channels'])[]).map((k) => (
              <Toggle key={k} label={tr(`CL-SET-CH-${k.toUpperCase().replace('_', '-')}`)} testId={`set-ch-${k}`} on={notif!.channels[k]} disabled={!online.value} onChange={(v) => setChannels({ [k]: v })} />
            )) : null}
            <Toggle label={tr('CL-SET-ADS')} hint={tr('CL-SET-ADS-HINT')} testId="set-ads" on={ads} onChange={(v) => { setAds(v); storage.set('ads', v ? 'on' : 'off'); sync(); }} />
            <Toggle label={tr('CL-SET-HIDE-CHRONICLE')} hint={tr('CL-SET-HIDE-CHRONICLE-HINT')} testId="set-chronicle" on={chronicle} disabled={!online.value}
              onChange={(v) => { setChronicle(v); app.call('updateMe', { body: { hideFromChronicle: v } }).then((p) => { player.value = p; }).catch((e) => { setChronicle(!v); app.handleError(e); }); }} />
          </Group>
          <Group title={tr('CL-SET-HELP')}>
            <Link label={tr('CL-SET-ACCOUNT')} testId="set-account" onClick={() => app.nav.openService('SCR-ACCOUNT')} />
            <Link label={tr('CL-SET-SUPPORT')} testId="set-support" onClick={() => app.nav.openService('SCR-SUPPORT')} />
            {/* Документы — только когда сервер прислал ссылки или ждёт согласия (решение владельца: пока не показываем). */}
            {Object.keys(legalUrls()).length || (storage.getJson<unknown[]>('consentsRequired') ?? []).length
              ? <Link label={tr('CL-SET-LEGAL')} testId="set-legal" onClick={() => app.nav.openService('SCR-LEGAL')} />
              : <Link label={tr('CL-SET-LEGAL')} testId="set-legal" right={tr('CL-COOP-SOON')} disabled onClick={() => {}} />}
          </Group>
          <p class="small muted" style={{ textAlign: 'center' }}>{tr('CL-SET-ABOUT', { build: CLIENT_BUILD, balance: typeof legal === 'string' || typeof legal === 'number' ? String(legal) : '—' })}</p>
        </div>
      </div>
    </>
  );
}

/** Экономия батареи: 30 к/с у сцены PixiJS (ux/11 §2.1). */
export function applyFps(): void {
  const s = stage.value as unknown as { app?: { ticker: { maxFPS: number } } } | null;
  if (s?.app) s.app.ticker.maxFPS = batterySaver() ? 30 : 0;
}

// ---------- SCR-ACCOUNT: ID, выгрузка данных, удаление (Р-90) ----------
const EXPORT_KEY = 'exportId';
const DELETE_KEY = 'deleteScheduledAt';
const fmtDate = (ms: number) => `${new Date(ms).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' })} ${mskTime(ms)}`;

export function AccountScreen(_p: ScreenProps) {
  const app = useApp();
  const me = player.value;
  const [exp, setExp] = useState<{ status: 'queued' | 'ready' | 'expired'; url?: string; readyBy?: string } | null>(null);
  const [scheduled, setScheduled] = useState<string | null>(me?.deleteScheduledAt ?? storage.get(DELETE_KEY));
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [keyDel] = useState(() => app.idem());
  const [keyExp] = useState(() => app.idem());
  useActionBar({ back: true });
  useEffect(() => {
    const id = storage.get(EXPORT_KEY);
    if (id) app.call('getExport', { params: { exportId: id } }).then((r) => setExp({ status: r.status, ...(r.url ? { url: r.url } : {}) })).catch(() => storage.remove(EXPORT_KEY));
  }, []);
  const err = (e: unknown) => {
    const code = e instanceof ApiCallError ? e.code : null;
    const at = e instanceof ApiCallError ? e.details?.['availableAt'] : null;
    if (code === 'DAILY_LIMIT') setNote(tr('CL-ACC-EXPORT-LIMIT', { date: typeof at === 'string' ? fmtDate(Date.parse(at)) : '—' }));
    else if (code === 'HAS_PENDING_PAYMENTS') setNote(tr('CL-ACC-PENDING-PAY'));
    else if (code === 'DELETE_ALREADY_SCHEDULED') setNote(tr('CL-ACC-ALREADY'));
    else app.handleError(e);
  };
  const doExport = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await app.call('requestExport', { body: {}, idempotencyKey: keyExp });
      storage.set(EXPORT_KEY, r.exportId);
      setExp({ status: 'queued', readyBy: r.readyBy });
      showToast(tr('CL-ACC-EXPORT-QUEUED'), 'success');
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await app.call('deleteAccount', { body: { confirm: DELETE_CONFIRM }, idempotencyKey: keyDel });
      storage.set(DELETE_KEY, r.scheduledAt);
      setScheduled(r.scheduledAt);
      setTyped('');
      app.haptic('warning');
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const doCancel = async () => {
    setBusy(true); setNote(null);
    try { await app.call('cancelAccountDelete', { body: {} }); storage.remove(DELETE_KEY); setScheduled(null); showToast(tr('CL-ACC-CANCELLED'), 'success'); }
    catch (e) {
      if (e instanceof ApiCallError && e.code === 'NOT_SCHEDULED') { storage.remove(DELETE_KEY); setScheduled(null); showToast(tr('CL-ACC-NOTHING')); }
      else err(e);
    } finally { setBusy(false); }
  };
  const copyId = () => { void navigator.clipboard?.writeText(me?.publicId ?? '').then(() => showToast(tr('CL-CHAT-COPIED'))).catch(() => {}); };
  const word = DELETE_CONFIRM;
  return (
    <>
      <ScreenTitle>{tr('CL-SET-ACCOUNT')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-ACCOUNT">
        <div class="page col set-page">
          <button class="list-row set-row" data-testid="acc-copy-id" onClick={copyId}><span style={{ flex: 1 }}>{tr('CL-ACC-ID')}</span><b>{me?.publicId ?? '—'}</b></button>
          <section class="card col">
            <b>{tr('CL-ACC-EXPORT')}</b>
            <span class="small muted">{tr('CL-ACC-EXPORT-HINT')}</span>
            {exp?.status === 'ready' && exp.url ? <button class="btn accent" data-testid="acc-export-download" onClick={() => app.wa?.openLink?.(exp.url!) ?? window.open(exp.url, '_blank')}>{tr('CL-ACC-EXPORT-DOWNLOAD')}</button>
              : exp?.status === 'queued' ? <span class="small" data-testid="acc-export-queued">{tr('CL-ACC-EXPORT-WAIT', { date: exp.readyBy ? fmtDate(Date.parse(exp.readyBy)) : tr('CL-ACC-EXPORT-SOON') })}</span>
                : <button class="btn outline" data-testid="acc-export" disabled={busy || !online.value} onClick={doExport}>{tr('CL-ACC-EXPORT-GO')}</button>}
          </section>
          <section class="card col danger-card">
            <b>{tr('CL-ACC-DELETE')}</b>
            {scheduled ? (
              <>
                <span data-testid="acc-delete-scheduled">{tr('CL-ACC-DELETE-AT', { date: fmtDate(Date.parse(scheduled)) })}</span>
                <button class="btn accent" data-testid="acc-delete-cancel" disabled={busy || !online.value} onClick={doCancel}>{tr('CL-ACC-DELETE-CANCEL')}</button>
              </>
            ) : (
              <>
                <span class="small">{tr('CL-ACC-DELETE-HINT')}</span>
                <label class="col" style={{ gap: 4 }}><span class="small">{tr('CL-ACC-DELETE-TYPE', { word })}</span>
                  <input class="input" data-testid="acc-delete-word" value={typed} onInput={(e) => setTyped((e.target as HTMLInputElement).value)} /></label>
                <button class="btn danger" data-testid="acc-delete" disabled={busy || !online.value || typed.trim().toUpperCase() !== word} onClick={doDelete}>{tr('CL-ACC-DELETE-GO')}</button>
              </>
            )}
          </section>
          {note ? <span class="small danger-text" data-testid="acc-note">{note}</span> : null}
        </div>
      </div>
    </>
  );
}

// ---------- SCR-SUPPORT (ux/11 §2.4) ----------
export const FAQ = ['PURCHASE', 'ITEM', 'COOP-LEAVE', 'ODDS', 'ENERGY', 'TUTORIAL', 'NAME', 'DELETE'] as const;

/** Ссылка в бот поддержки: support_<ID игрока>__order_<id> (ux/11 §2.4). Имя бота — из ссылки игры. */
export function supportLink(publicId: string, orderId?: string | null, gameLink = GAME_LINK): string {
  const bot = /t\.me\/([A-Za-z0-9_]+)/.exec(gameLink)?.[1] ?? 'garages_game_bot';
  return `https://t.me/${bot}?start=support_${publicId}${orderId ? `__order_${orderId}` : ''}`;
}

export function SupportScreen(p: ScreenProps) {
  const app = useApp();
  const [open, setOpen] = useState<string | null>(null);
  useActionBar({ back: true, main: { text: tr('CL-SUP-WRITE'), testId: 'support-write', onClick: () => app.wa?.openTelegramLink?.(supportLink(player.value?.publicId ?? '', p.params['orderId'] as string | undefined)) } });
  return (
    <>
      <ScreenTitle>{tr('CL-SET-SUPPORT')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-SUPPORT">
        <div class="page col set-page">
          <div class="list">
            {FAQ.map((k) => (
              <button key={k} class="list-row set-row col faq" style={{ alignItems: 'stretch' }} data-testid={`faq-${k}`} onClick={() => setOpen(open === k ? null : k)} aria-expanded={open === k}>
                <span class="row"><b style={{ flex: 1 }}>{tr(`CL-FAQ-${k}-Q`)}</b>{open === k ? '−' : '+'}</span>
                {open === k ? <span class="small" style={{ textAlign: 'left' }}>{tr(`CL-FAQ-${k}-A`)}</span> : null}
              </button>
            ))}
          </div>
          <span class="small muted">{tr('CL-SUP-PAYSUPPORT')}</span>
        </div>
      </div>
    </>
  );
}

// ---------- SCR-LEGAL (ux/11 §2.5) ----------
export const LEGAL_DOCS = ['terms', 'privacy', 'chat_rules', 'refunds', 'age_rating', 'odds'] as const;
const LEGAL_KEY: Record<(typeof LEGAL_DOCS)[number], string> = { terms: 'CL-LEGAL-TERMS', privacy: 'CL-LEGAL-PRIVACY', chat_rules: 'CL-LEGAL-CHAT', refunds: 'CL-LEGAL-REFUNDS', age_rating: 'CL-LEGAL-AGE', odds: 'CL-LEGAL-ODDS' };
type ConsentDoc = { docId: 'terms' | 'privacy' | 'chat_rules'; version: string; url?: string };

/** Адрес документа: из согласий, которые прислал сервер, иначе — `clientConfig.values.legal.urls` (Р-К101). */
export function legalUrl(doc: string): string | null {
  const required = storage.getJson<ConsentDoc[]>('consentsRequired') ?? [];
  const r = required.find((d) => d.docId === doc);
  if (r?.url) return r.url;
  const urls = ((clientConfig.value['legal'] ?? {}) as { urls?: Record<string, string> }).urls ?? {};
  return urls[doc] ?? null;
}

export function LegalScreen(_p: ScreenProps) {
  const app = useApp();
  const [required, setRequired] = useState<ConsentDoc[]>(storage.getJson<ConsentDoc[]>('consentsRequired') ?? []);
  const [busy, setBusy] = useState(false);
  const [chatOk, setChatOk] = useState(chatRulesAccepted());
  useActionBar(required.length ? {
    back: true,
    main: { text: tr('CL-LEGAL-ACCEPT'), testId: 'legal-accept', disabled: busy || !online.value, onClick: async () => {
      setBusy(true);
      try {
        for (const d of required) {
          await app.call('acceptConsent', { body: { docId: d.docId, version: d.version } });
          if (d.docId === 'chat_rules') markChatRulesAccepted(d.version);
        }
        storage.remove('consentsRequired');
        setRequired([]);
        showToast(tr('CL-LEGAL-ACCEPTED'), 'success');
      } catch (e) { app.handleError(e); } finally { setBusy(false); }
    } },
  } : { back: true });
  return (
    <>
      <ScreenTitle>{tr('CL-SET-LEGAL')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-LEGAL">
        <div class="page col set-page">
          {required.length ? <div class="banner warn" data-testid="legal-updated">{tr('CL-LEGAL-UPDATED')}</div> : null}
          <div class="list">
            {LEGAL_DOCS.map((d) => {
              const url = legalUrl(d);
              const req = required.find((x) => x.docId === d);
              return (
                <button key={d} class="list-row set-row" data-testid={`legal-${d}`} disabled={!url && d !== 'odds'}
                  onClick={() => { if (d === 'odds' && !url) showToast(tr('CL-LEGAL-ODDS-TEXT')); else if (url) app.wa?.openLink?.(url); }}>
                  <span class="col" style={{ flex: 1, gap: 2 }}><span>{tr(LEGAL_KEY[d])}</span>{req ? <span class="small warn-text">{tr('CL-LEGAL-NEW-VERSION', { v: req.version })}</span> : !url && d !== 'odds' ? <span class="small muted">{tr('CL-LEGAL-SOON')}</span> : null}</span>›
                </button>
              );
            })}
          </div>
          {!chatOk ? <div class="card col" data-testid="legal-chat-consent"><b>{tr('CL-LEGAL-CHAT')}</b><ChatRulesConsent onDone={() => { setChatOk(true); showToast(tr('CL-LEGAL-ACCEPTED'), 'success'); }} /></div> : null}
        </div>
      </div>
    </>
  );
}
